/**
 * A user's appearance (Settings → Appearance), saved per user on the server (accounts 0022 `dashboard_appearance`,
 * GET/PUT /api/dashboard/settings/appearance) and inlined by the Worker into the dashboard shell before first paint:
 * the <html> attributes, the saved custom theme's <style>, and a tiny script for "Match my device".
 * DOM-free (the Worker imports it); the browser side is runtime.ts.
 */
import { DARK_THEME, THEME_IDS, cleanCustomTheme, customThemeCss, type CustomTheme, type ThemeId } from "./themes";

export interface Appearance {
  theme: ThemeId;
  custom: CustomTheme | null;
  matchDevice: boolean;
  highContrast: boolean;
  reduceMotion: boolean;
}
export const DEFAULT_APPEARANCE: Appearance = { theme: "civic", custom: null, matchDevice: false, highContrast: false, reduceMotion: false };

/** A stored or sent appearance → a clean one (unknown keys dropped; "custom" without a custom theme falls back to civic). */
export function cleanAppearance(x: unknown, prev: Appearance = DEFAULT_APPEARANCE): Appearance {
  const o = (x && typeof x === "object" ? x : {}) as Record<string, unknown>;
  const custom = "custom" in o ? (o.custom === null ? null : cleanCustomTheme(o.custom) ?? prev.custom) : prev.custom;
  const theme = THEME_IDS.includes(o.theme as ThemeId) ? (o.theme as ThemeId) : prev.theme;
  const bool = (k: "matchDevice" | "highContrast" | "reduceMotion") => (typeof o[k] === "boolean" ? (o[k] as boolean) : prev[k]);
  return { theme: theme === "custom" && !custom ? "civic" : theme, custom, matchDevice: bool("matchDevice"), highContrast: bool("highContrast"), reduceMotion: bool("reduceMotion") };
}

/** The theme on screen: Graphite while "Match my device" is on and the device is dark. */
export const shownTheme = (a: Appearance, deviceDark: boolean): ThemeId => (a.matchDevice && deviceDark ? DARK_THEME : a.theme);

/** Attributes for <html> (the Worker sets them in the served HTML; runtime.ts keeps them current). */
export function htmlAttributes(a: Appearance): Record<string, string> {
  const out: Record<string, string> = { "data-theme": a.theme };
  if (a.matchDevice) out["data-match-device"] = "";
  if (a.highContrast) out["data-contrast"] = "high";
  if (a.reduceMotion) out["data-motion"] = "reduce";
  return out;
}

/** Runs before first paint: "Match my device" picks Graphite while the device is in dark mode. */
export const MATCH_DEVICE_SCRIPT = `(function(){var d=document.documentElement;if(d.hasAttribute("data-match-device")&&matchMedia("(prefers-color-scheme: dark)").matches)d.setAttribute("data-theme","${DARK_THEME}")})()`;

const escapeStyle = (css: string) => css.replace(/<\/?style/gi, "");
/** What the Worker appends to <head>: the saved custom theme (even while another theme is shown) and the match-device script. */
export function appearanceHead(a: Appearance): string {
  return `<style id="theme-custom">${a.custom ? escapeStyle(customThemeCss(a.custom)) : ""}</style>`
    + (a.matchDevice ? `<script>${MATCH_DEVICE_SCRIPT}</script>` : "");
}
